import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { getLicensesOverview } from '../api';
import { useAuth } from '../context/AuthContext';
import SearchInput from '../components/SearchInput';
import LoadingState from '../components/LoadingState';
import { Svg, initialsOf, useSort, sortBy, SortHead } from '../components/PortalUI';
import '../components/PortalUI.css';

const TIER_LABEL = { trial: 'Trial', standard: 'Standard', premium: 'Premium' };
const monthText = (m) => { const [y, mo] = m.split('-').map(Number); return new Date(y, mo - 1, 1).toLocaleDateString('da-DK', { month: 'long', year: 'numeric' }); };
const num = (n) => (n === null || n === undefined ? '—' : n);

// Licenser in the Provider portal: every organisation, its customers and what each has: the basis for billing. The numbers are kept
// once a month, so a month that has passed shows what it was then.
export default function LicensesPage() {
  const { permissions, isOwner } = useAuth();
  const allowed = isOwner || permissions.includes('licenses');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [month, setMonth] = useState('');
  const [open, setOpen] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [tier, setTier] = useState('all');
  const [sort, toggleSort] = useSort('users', 'desc');

  useEffect(() => {
    if (!allowed) return;
    setData(null);
    setError('');
    getLicensesOverview(month).then(setData).catch((err) => setError(err.message));
  }, [allowed, month]);

  const groups = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    const byOrg = new Map();
    data.rows.forEach((r) => {
      if (tier !== 'all' && r.tier !== tier) return;
      if (q && !`${r.orgName} ${r.name}`.toLowerCase().includes(q)) return;
      const key = r.orgId || '_none';
      if (!byOrg.has(key)) byOrg.set(key, { key, name: r.orgName, rows: [] });
      byOrg.get(key).rows.push(r);
    });
    const list = [...byOrg.values()].map((g) => ({
      ...g,
      rows: g.rows.sort((a, b) => a.name.localeCompare(b.name, 'da')),
      count: g.rows.length,
      premium: g.rows.filter((r) => r.tier === 'premium').length,
      standard: g.rows.filter((r) => r.tier === 'standard').length,
      trial: g.rows.filter((r) => r.tier === 'trial').length,
      users: g.rows.reduce((n, r) => n + (r.licensedUsers || 0), 0),
    }));
    return sortBy(list.sort((a, b) => a.name.localeCompare(b.name, 'da')), sort, {
      name: (g) => g.name,
      tiers: (g) => g.premium * 1000000 + g.standard * 1000 + g.trial,
      count: (g) => g.count,
      users: (g) => g.users,
    });
  }, [data, search, tier, sort]);

  if (!allowed) return <Navigate to="/organizations" replace />;

  const total = groups.reduce((t, g) => ({ count: t.count + g.count, premium: t.premium + g.premium, standard: t.standard + g.standard, trial: t.trial + g.trial, users: t.users + g.users }), { count: 0, premium: 0, standard: 0, trial: 0, users: 0 });
  const toggle = (key) => setOpen((prev) => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  const months = data ? [...new Set([...data.months, data.month])].sort().reverse() : [];

  function exportCsv() {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [['Måned', 'Organisation', 'Kunde', 'Licens', 'Licenserede brugere', 'Tilføjet'].map(esc).join(';')];
    groups.forEach((g) => g.rows.forEach((r) => lines.push([data.month, g.name, r.name, TIER_LABEL[r.tier] || r.tier, r.licensedUsers ?? '', r.addedAt ? r.addedAt.slice(0, 10) : ''].map(esc).join(';'))));
    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `nordcomply-licenser-${data.month}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  const tiersText = (g) => [g.premium && `${g.premium} Premium`, g.standard && `${g.standard} Standard`, g.trial && `${g.trial} Trial`].filter(Boolean).join(' · ') || '—';

  return (
    <div className="pu-page">
      <div className="pu-head">
        <div><h1>Licenser</h1><p>Alle organisationer, deres kunder og licenser. Grundlag for fakturering.</p></div>
        <span className="pl-acts">
          {data && (
            <select className="pu-sel" value={data.month} onChange={(e) => setMonth(e.target.value)} aria-label="Måned">
              {months.map((m) => (<option key={m} value={m}>{monthText(m)}</option>))}
            </select>
          )}
          <button type="button" className="pu-btn" onClick={exportCsv} disabled={!data}>Eksportér CSV</button>
        </span>
      </div>
      {error && <p className="state-error">{error}</p>}
      {!data && !error && <LoadingState />}
      {data && (
        <>
          <div className="pl-cards">
            <div className="pl-card"><small>Organisationer</small><b>{groups.length}</b></div>
            <div className="pl-card"><small>Kunder</small><b>{total.count}</b></div>
            <div className="pl-card pl-card--t"><small>Licenser</small><div className="pl-tiers"><div><b>{total.premium}</b><span>Premium</span></div><div><b>{total.standard}</b><span>Standard</span></div><div><b>{total.trial}</b><span>Trial</span></div></div></div>
            <div className="pl-card"><small>Licenserede brugere</small><b>{total.users}</b></div>
          </div>
          <div className="pu-bar">
            <SearchInput value={search} onChange={setSearch} placeholder="Søg efter organisation eller kunde..." />
            <div className="pu-seg" role="group" aria-label="Licens">
              {[['all', 'Alle'], ['premium', 'Premium'], ['standard', 'Standard'], ['trial', 'Trial']].map(([k, l]) => (
                <button key={k} type="button" className={tier === k ? 'is-on' : ''} onClick={() => setTier(k)}>{l}</button>
              ))}
            </div>
          </div>
          <div className="pu-table pl-table">
            <div className="pu-th pu-tr pl-tr"><SortHead label="Organisation" k="name" sort={sort} onSort={toggleSort} /><SortHead label="Licenser" k="tiers" sort={sort} onSort={toggleSort} /><span className="pl-r"><SortHead label="Kunder" k="count" sort={sort} onSort={toggleSort} /></span><span className="pl-r"><SortHead label="Licenserede brugere" k="users" sort={sort} onSort={toggleSort} /></span></div>
            <div className="pl-scroll">
            {groups.length === 0 && <div className="pu-empty"><p>Ingen organisationer matcher</p></div>}
            {groups.map((g) => {
              const isOpen = open.has(g.key);
              return (
                <div key={g.key} className="pl-grp">
                  <div className="pu-tr pl-tr pl-org pu-tr--click" role="button" tabIndex={0} onClick={() => toggle(g.key)} onKeyDown={(e) => { if (e.key === 'Enter') toggle(g.key); }}>
                    <span className="pu-who"><span className="pl-chev"><Svg name={isOpen ? 'down' : 'chevron'} size={15} /></span><span className="pu-av">{initialsOf(g.name)}</span><span><b>{g.name}</b></span></span>
                    <span className="pu-c">{tiersText(g)}</span>
                    <span className="pl-n">{g.count}</span>
                    <span className="pl-n pl-n--s">{g.users}</span>
                  </div>
                  {isOpen && g.rows.map((r) => (
                    <div key={r.tenantId} className="pu-tr pl-tr pl-cust">
                      <span className="pu-who"><span className="pl-pad" /><span className="pl-pad" /><span><b>{r.name}</b>{r.addedAt && <small>Tilføjet {new Date(r.addedAt).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })}</small>}</span></span>
                      <span>
                        <span className={`pu-chip ${r.tier === 'premium' ? 'pu-chip--adm' : r.tier === 'trial' ? 'pu-chip--warn' : 'pu-chip--neu'}`}>{TIER_LABEL[r.tier] || r.tier}</span>
                        {r.tier === 'trial' && <small className="pl-sm">{r.expired ? 'Udløbet' : r.daysLeft != null ? `${r.daysLeft} dage tilbage` : ''}</small>}
                      </span>
                      <span />
                      <span className="pl-n">{num(r.licensedUsers)}</span>
                    </div>
                  ))}
                </div>
              );
            })}
            </div>
            <div className="pu-tr pl-tr pl-tot"><span><b>I alt</b></span><span className="pu-c">{[total.premium && `${total.premium} Premium`, total.standard && `${total.standard} Standard`, total.trial && `${total.trial} Trial`].filter(Boolean).join(' · ') || '—'}</span><span className="pl-n pl-n--s">{total.count}</span><span className="pl-n pl-n--s">{total.users}</span></div>
          </div>
        </>
      )}
    </div>
  );
}
